/*
 * Exports (both sides) and imports (company side): what may be exported or
 * imported, the person's own history, and files that are "built in the
 * background" — a request finishes a few seconds after it is made, so the
 * screens' polling sees it go from running to done.
 */
// Some admin screens ask the tenant prefix for their types; the API answers for
// whoever is signed in, so the list follows the side (portal), not the prefix.
import { ok, fail, notFound, paginate, nextId, hoursAgo, portal } from "./util";

const ME = "Jane Wanjiku";
const ADMIN = "Platform Admin";
const BUILD_MS = 3500;

/* ------------------------------------------------------------------ */
/* Exports (config/exports.php, ManagesExports, ExportResource)         */
/* ------------------------------------------------------------------ */

const EXPORT_TYPES = {
  tenant: [
    ["prospects", "Prospects"], ["leads", "Leads"], ["opportunities", "Opportunities"], ["quotations", "Quotations"],
    ["customers", "Customers"], ["contacts", "Contacts"], ["contracts", "Contracts"], ["tickets", "Support Tickets"],
    ["renewals", "Renewals"], ["users", "Users"], ["roles", "Roles"], ["integration-requests", "Integration Requests"],
  ],
  admin: [
    ["tenants", "Companies"], ["trial-applications", "Trial Applications"], ["client-usage", "Client Usage"],
    ["website-visits", "Website Visits"], ["plans", "Plans"], ["payment-methods", "Payment Methods"],
    ["payment-schedules", "Payment Schedules"], ["module-catalogue", "Modules Catalogue"],
  ],
};
const exportLabel = (key) => [...EXPORT_TYPES.tenant, ...EXPORT_TYPES.admin].find(([k]) => k === key)?.[1];

const X = (id, key, status, rows, hours, extra = {}) => {
  const created = hoursAgo(hours);
  return {
    id, reference: `EXP-2026-${String(400 + id).padStart(6, "0")}`, export_key: key, label: exportLabel(key), filters: {},
    format: "xlsx", status, file_name: `${key}-${created.slice(0, 10)}.xlsx`, size_bytes: rows * 180 + 4096, row_count: rows,
    requested_by: extra.requested_by ?? ME, started_at: created, completed_at: status === "completed" ? hoursAgo(hours - 0.01) : null,
    expires_at: hoursAgo(hours - 7 * 24), created_at: created, error_message: extra.error_message, _ready_at: 0,
  };
};

const exportsBy = {
  tenant: [
    X(1, "prospects", "completed", 2410, 2),
    X(2, "quotations", "completed", 311, 26),
    X(3, "tickets", "failed", 0, 50, { error_message: "The export took too long. Narrow the dates and try again." }),
    X(4, "roles", "completed", 10, 150),
    X(5, "customers", "completed", 218, 24 * 9),
  ],
  admin: [
    X(1, "tenants", "completed", 4, 3, { requested_by: ADMIN }),
    X(2, "client-usage", "completed", 4, 30, { requested_by: ADMIN }),
    X(3, "trial-applications", "completed", 23, 24 * 8, { requested_by: ADMIN }),
  ],
};

// Older than the 7-day link: the file is gone.
const expired = (x) => new Date(x.expires_at).getTime() < Date.now();

/** ExportResource, with a fresh request finishing once its build time has passed. */
const presentExport = (x) => {
  if (x.status === "processing" && Date.now() >= x._ready_at) {
    x.status = "completed";
    x.completed_at = new Date().toISOString();
  }
  const { _ready_at, error_message, ...rest } = x;
  return {
    ...rest,
    ...(x.status === "failed" ? { error_message } : {}),
    is_downloadable: x.status === "completed" && !expired(x),
  };
};

// A link the browser can open: a blob URL in the page (Chrome blocks opening data: URLs), a data: URL elsewhere.
const fileUrl = (text, type) =>
  typeof Blob !== "undefined" && typeof URL?.createObjectURL === "function" && typeof window !== "undefined"
    ? URL.createObjectURL(new Blob([text], { type }))
    : `data:${type};charset=utf-8,${encodeURIComponent(text)}`;

const sideOf = (prefix) => (prefix === "admin" ? "admin" : "tenant");

const exportRoutes = (prefix) => {
  const side = sideOf(prefix);
  const find = (id) => exportsBy[side].find((x) => String(x.id) === String(id));

  return [
    ["get", `${prefix}/export-types`, () =>
      ok(EXPORT_TYPES[prefix === "admin" ? "admin" : portal()].map(([key, label]) => ({ key, label })))],

    ["get", `${prefix}/exports/:id/download`, ({ params }) => {
      const x = find(params.id);
      if (!x) return notFound(`Export ${params.id} was not found.`);
      const shown = presentExport(x);
      if (!shown.is_downloadable) return fail(x.status === "completed" ? "This export has expired." : `This export is ${x.status}.`, null, 409);
      const csv = `${x.label}\nSample export (prototype mode): ${x.row_count} rows in the real file.\n`;
      return ok({ url: fileUrl(csv, "text/csv"), file_name: x.file_name, size_bytes: x.size_bytes, row_count: x.row_count, expires_in: 600 });
    }],

    ["get", `${prefix}/exports/:id`, ({ params }) => {
      const x = find(params.id);
      return x ? ok(presentExport(x)) : notFound(`Export ${params.id} was not found.`);
    }],

    ["get", `${prefix}/exports`, ({ query }) => {
      const rows = exportsBy[side]
        .map(presentExport)
        .filter((x) => !query.status || x.status === query.status)
        .sort((a, b) => b.created_at.localeCompare(a.created_at));
      const page = paginate(rows, query, 25);
      // A resource collection's paginated response: data, links, meta.
      return ok({
        data: page.data,
        links: { first: page.first_page_url, last: page.last_page_url, prev: page.prev_page_url, next: page.next_page_url },
        meta: { current_page: page.current_page, from: page.from, last_page: page.last_page, links: [], path: "", per_page: page.per_page, to: page.to, total: page.total },
      });
    }],

    ["post", `${prefix}/exports`, ({ body }) => {
      const label = exportLabel(body.type);
      if (!label) return fail("Validation failed", { type: ["The selected type is invalid."] }, 422);
      const x = X(nextId(exportsBy[side]), body.type, "processing", 40 + Math.floor(Math.random() * 400), 0, { requested_by: side === "admin" ? ADMIN : ME });
      x.filters = body.filters ?? {};
      x.completed_at = null;
      x._ready_at = Date.now() + BUILD_MS;
      exportsBy[side].push(x);
      return ok(presentExport(x), "Export queued. We will email you when it is ready.", 202);
    }],
  ];
};

/* ------------------------------------------------------------------ */
/* Imports (config/imports.php, ImportController, MigrationChecklist)   */
/* ------------------------------------------------------------------ */

// What each upload carries: the template's columns, and a few sample rows.
const IMPORT_TYPES = {
  users: {
    label: "Users",
    columns: [["Full name", "name"], ["Email", "email"], ["Phone", "phone"], ["Role", "role"], ["Team", "team"]],
    rows: [
      { name: "Joseph Mutiso", email: "joseph.mutiso@demulla.co.ke", phone: "+254 708 551 744", role: "Support", team: "Service Desk" },
      { name: "Halima Abdi", email: "halima.abdi@demulla.co.ke", phone: "+254 725 119 863", role: "Implementation", team: "Implementation Squad A" },
      { name: "Kevin Mwangi", email: "kevin.mwangi@demulla.co.ke", phone: "", role: "Implementation", team: "" },
    ],
    errors: { 3: "The email address is not valid." },
  },
  prospects: {
    label: "Prospects",
    columns: [["Organisation", "organisation"], ["Contact name", "contact_name"], ["Contact title", "contact_title"], ["Phone", "phone"], ["Email", "email"],
      ["Industry", "industry"], ["Segment", "segment"], ["Location", "location"], ["Staff count", "staff_count"], ["Estimated value", "estimated_value"],
      ["Products", "products"], ["Source", "source"], ["Campaign", "campaign"], ["Owner", "owner"], ["Not a duplicate because", "not_duplicate_reason"]],
    rows: [
      { organisation: "Kenya Fresh Dairies", contact_name: "Agnes Mumbi", phone: "+254 722 111 222", industry: "Agriculture", segment: "SME", location: "Westlands", source: "Event", campaign: "HR Summit 2026" },
      { organisation: "Lakeview SACCO", contact_name: "Rose Akinyi", phone: "+254 713 321 152", industry: "SACCO", segment: "MID", location: "Milimani", source: "Event", campaign: "HR Summit 2026" },
      { organisation: "Bahari Fisheries", contact_name: "Omar Hamisi", phone: "", industry: "Agriculture", segment: "SME", location: "Nyali", source: "Event" },
      { organisation: "Mlima Schools", contact_name: "Grace Kerubo", phone: "+254 701 555 010", industry: "Educaton", segment: "SME", location: "Nakuru CBD", source: "Event" },
      { organisation: "Delta Couriers", contact_name: "Samuel Oketch", phone: "+254 790 888 432", industry: "Logistics", segment: "MID", location: "Nyali", source: "Event" },
      { organisation: "Uhuru Clinic", contact_name: "Dr. Joy Naliaka", phone: "+254 711 909 300", industry: "Healthcare", segment: "SME", location: "Upper Hill", source: "Event" },
    ],
    errors: {
      2: "Possible duplicate of PRS-0104 (Lakeview SACCO) by organisation name. Fill \"Not a duplicate because\" to import it anyway.",
      3: "Give a phone number or an email.",
      4: "Industry \"Educaton\" is not on the list. Use one from the Industries sheet.",
    },
  },
  leads: {
    label: "Leads",
    columns: [["Company", "name"], ["Contact", "contact"], ["Product", "product"], ["Value", "value"], ["Owner", "owner"]],
    rows: [
      { name: "Savannah Hotels Group", contact: "Ruth Kamau", product: "HR & Payroll", value: "780000", owner: "Jane Wanjiku" },
      { name: "Pwani Logistics", contact: "Hassan Ali", product: "CRM Business", value: "180000", owner: "Peter Ouma" },
    ],
    errors: {},
  },
  customers: {
    label: "Customers",
    columns: [["Customer", "name"], ["Industry", "industry"], ["Segment", "segment"], ["Customer since", "since"], ["Account manager", "manager"]],
    rows: [
      { name: "Harambee Sacco Society", industry: "SACCO", segment: "ENT", since: "2025-02-01", manager: "Brian Otieno" },
      { name: "Savannah Hotels Group", industry: "Hospitality", segment: "MID", since: "2024-11-15", manager: "Jane Wanjiku" },
      { name: "Kampala Retail Hub", industry: "Retail", segment: "XL", since: "2025-04-01", manager: "Ann Mutua" },
    ],
    errors: { 3: "\"XL\" is not a segment here. Use SME, MID or ENT." },
  },
  contacts: {
    label: "Contacts",
    columns: [["Customer", "customer"], ["Name", "name"], ["Title", "title"], ["Email", "email"], ["Phone", "phone"]],
    rows: [
      { customer: "Harambee Sacco Society", name: "Philip Kiptoo", title: "CEO", email: "pkiptoo@harambeesacco.co.ke", phone: "+254 733 990 112" },
      { customer: "Pwani Logistics", name: "Hassan Ali", title: "Operations Director", email: "hassan@pwanilogistics.co.ke", phone: "+254 722 300 451" },
    ],
    errors: {},
  },
  contracts: {
    label: "Contracts",
    columns: [["Customer", "customer"], ["Product", "product"], ["Start", "start"], ["End", "end"], ["Annual value", "value"]],
    rows: [
      { customer: "Harambee Sacco Society", product: "CRM Enterprise", start: "2025-02-01", end: "2028-01-31", value: "2640000" },
      { customer: "Savannah Hotels Group", product: "HR & Payroll", start: "2024-11-15", end: "2025-11-14", value: "780000" },
    ],
    errors: {},
  },
  products: {
    label: "Products",
    columns: [["Code", "code"], ["Name", "name"], ["Category", "category"], ["List price", "price"]],
    rows: [
      { code: "CRM-BUS", name: "CRM Business", category: "Subscription", price: "65000" },
      { code: "IMP-STD", name: "Standard implementation", category: "Services", price: "250000" },
    ],
    errors: {},
  },
};

const typeList = () => Object.entries(IMPORT_TYPES).map(([key, t]) => ({ key, label: t.label }));

const checklist = () => {
  const step = (key, title, description, imports, screen, done, total, unit, optional = false) => ({
    key, title, description, imports: imports.map((k) => ({ key: k, label: IMPORT_TYPES[k].label })), screen,
    progress: { done, total, unit, state: done === 0 ? "empty" : total !== null && done < total ? "partial" : "done" }, optional,
  });
  return [
    step("users", "Users", "Everybody who will sign in, with the role each starts with. Each is sent an invitation.", ["users"], "system-administration.users", 16, null, "users"),
    step("products", "Products", "What you sell, with list prices, so leads and quotations can name them.", ["products"], "settings.products", 6, null, "products"),
    step("customers", "Customers and contacts", "The companies that already buy from you, and the people you deal with there.", ["customers", "contacts"], "customers.list", 218, 240, "customers"),
    step("contracts", "Contracts", "Each customer's current contract, so renewals are tracked from day one.", ["contracts"], "contracts.list", 0, 218, "contracts"),
    step("pipeline", "Open pipeline", "Prospects and leads still being worked, with their owners.", ["prospects", "leads"], "prospects.list", 2410, null, "prospects", true),
  ];
};

const I = (id, key, mode, status, hours, counts = {}) => {
  const t = IMPORT_TYPES[key];
  return {
    id, reference: `IMP-2026-${String(100 + id).padStart(6, "0")}`, import_key: key, label: t.label,
    file_name: `${key}-${mode === "check" ? "draft" : "final"}.xlsx`, mode, status,
    total_rows: counts.total ?? t.rows.length, valid_rows: counts.valid ?? t.rows.length - Object.keys(t.errors).length,
    imported_rows: counts.imported ?? null, failed_rows: counts.failed ?? Object.keys(t.errors).length,
    errors: Object.entries(t.errors).map(([line, message]) => ({ line: Number(line) + 1, message })),
    error_message: counts.error_message ?? null, user_name: ME, created_at: hoursAgo(hours), completed_at: hoursAgo(hours - 0.02),
    _ready_at: 0, _kept: true,
  };
};

const imports = [
  I(1, "users", "import", "completed", 24 * 20, { imported: 14, failed: 0, total: 14, valid: 14 }),
  I(2, "customers", "check", "checked", 5),
  I(3, "prospects", "import", "completed", 30, { imported: 3, failed: 1 }),
  I(4, "contracts", "import", "failed", 24 * 3, { imported: 0, error_message: "The file could not be read. Save it as .xlsx from the template and try again." }),
];
imports[0].errors = [];

const rowsFor = (key) => {
  const t = IMPORT_TYPES[key];
  return t.rows.map((data, i) => ({ line: i + 2, valid: !t.errors[i + 1], message: t.errors[i + 1] ?? null, data }));
};

/** ImportController::present, moving a background run to its outcome once its time is up. */
const presentImport = (row, withPreview = false) => {
  if ((row.status === "pending" || row.status === "processing") && Date.now() >= row._ready_at) {
    row.status = row.mode === "check" ? "checked" : "completed";
    row.completed_at = new Date().toISOString();
    if (row.mode !== "check") row.imported_rows = row.valid_rows;
  } else if (row.status === "pending" && Date.now() >= row._ready_at - BUILD_MS / 2) {
    row.status = "processing";
  }
  const { _ready_at, _kept, ...rest } = row;
  const t = IMPORT_TYPES[row.import_key];
  return {
    ...rest,
    can_import: row.status === "checked" && row.valid_rows > 0 && _kept,
    preview: withPreview && row.status === "checked"
      ? { columns: t.columns.map(([label]) => label), keys: t.columns.map(([, key]) => key), rows: rowsFor(row.import_key) }
      : null,
  };
};

const queue = (key, file, mode) => {
  const row = I(nextId(imports), key, mode, "pending", 0);
  row.file_name = file?.name ?? row.file_name;
  row.imported_rows = null;
  row.completed_at = null;
  row._ready_at = Date.now() + BUILD_MS;
  imports.push(row);
  return row;
};

const unknownImport = (key) => fail(`There is no import called "${key}".`, null, 409);

// The template is a binary download: a Blob in the browser, plain text elsewhere.
const templateFor = (key) => {
  const t = IMPORT_TYPES[key];
  const csv = `${t.columns.map(([label]) => label).join(",")}\n`;
  return typeof Blob !== "undefined" ? new Blob([csv], { type: "text/csv" }) : csv;
};

const importRoutes = [
  ["get", "tenant/import-types", () => ok(typeList())],
  ["get", "tenant/imports-checklist", () => ok({ steps: checklist() })],

  ["get", "tenant/imports", ({ query }) => {
    const rows = imports
      .map((row) => presentImport(row))
      .filter((r) => (!query.type || r.import_key === query.type) && (!query.status || r.status === query.status))
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
    return ok(paginate(rows, query, 25));
  }],

  ["post", "tenant/imports/:id/run", ({ params }) => {
    const row = imports.find((r) => String(r.id) === String(params.id));
    if (!row) return notFound(`Import ${params.id} was not found.`);
    presentImport(row);
    if (row.status !== "checked") return fail(`${row.reference} is not waiting to be imported, so there is nothing to import from it.`, null, 409);
    if (!row.valid_rows) return fail("None of the rows in that file can be imported. Correct it and check it again.", null, 409);
    row.mode = "import";
    row.status = "processing";
    row._ready_at = Date.now() + BUILD_MS;
    return ok(presentImport(row), `Importing ${row.valid_rows} rows (${row.reference}). We will email demo@deecrm360.test once it is done.`);
  }],

  ["get", "tenant/imports/:key/template", ({ params }) =>
    IMPORT_TYPES[params.key] ? [200, templateFor(params.key)] : unknownImport(params.key)],

  ["post", "tenant/imports/:key/preview", ({ params, body }) => {
    if (!IMPORT_TYPES[params.key]) return unknownImport(params.key);
    const row = queue(params.key, body.file, "check");
    return ok(presentImport(row), `Your file is being checked (${row.reference}). We will email demo@deecrm360.test once it is done, and you can follow it under My Imports. Nothing is saved until you confirm.`, 202);
  }],

  ["get", "tenant/imports/:id", ({ params }) => {
    const row = imports.find((r) => String(r.id) === String(params.id));
    return row ? ok(presentImport(row, true)) : notFound(`Import ${params.id} was not found.`);
  }],

  ["post", "tenant/imports/:key", ({ params, body }) => {
    if (!IMPORT_TYPES[params.key]) return unknownImport(params.key);
    const row = queue(params.key, body.file, "import");
    return ok(presentImport(row), `Your file is being imported (${row.reference}). We will email demo@deecrm360.test once it is done, and you can follow it under My Imports.`, 202);
  }],
];

export default [...exportRoutes("tenant"), ...exportRoutes("admin"), ...importRoutes];
